import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnChanges,
  OnDestroy,
  signal,
  SimpleChanges,
  WritableSignal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import * as L from "leaflet";
import "leaflet.markercluster";
import { NgClass, NgStyle } from "@angular/common";
import { AssetService } from "../../../services/api/asset/asset.service";
import { ASSET_TYPES, AssetType } from "../../../enums/assetTypes.enum";

import { first, Subscription } from "rxjs";

import { SvgIconComponent } from "../../components/svg-icon/svg-icon.component";
import { environment } from "../../../../environments/environment";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { Router } from "@angular/router";
import { PermissionService } from "../../../services/api/permission/permission.service";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import { createTimeoutSignal } from "../../utils/timeout.utils";
import { TimeoutComponent } from "../../components/timeout/timeout.component";

import { FloorMapPoint } from "../../../models/ui/floorMapPoint";
import { Position } from "../../../models/shared/position";
import { AssetHierarchyDTO } from "../../../models/api/asset/assetHierarchyDTO";
import { Building } from "../../../models/api/asset/building";
import { Location } from "../../../models/api/asset/location";
import { IMAGE_PATTERN, PDF_PATTERN } from "../../../constants/regExp";
import { SupportedLang } from "../../../models/ui/supportedLangs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SEVERITY } from "../../../constants/severities";
import { AuthService } from "../../../services/api/auth/auth.service";
import { USER_ROLES } from "../../../enums/userRoles.enum";
import { AssetHierarchyService } from "../../../services/api/asset-hierarchy/asset-hierarchy.service";
import { RealtimeSeverity } from "../../../models/ws/realtimeSeverity";
import { SeveritySubscriptionHandle, SeverityService } from "../../../services/websocket/severity/severity.service";

export class BotevoMarker extends L.Marker {
  status!: string | undefined;
  constructor(latLng: L.LatLngExpression, options?: L.MarkerOptions, status?: string) {
    super(latLng, options);
    this.status = status;
  }
}
@Component({
  standalone: true,
  selector: "app-customer-map",
  imports: [TranslateModule, NgStyle, NgClass, SvgIconComponent, ProgressSpinnerModule, TimeoutComponent],
  templateUrl: "./customer-map.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./customer-map.component.scss",
})
export class CustomerMapComponent implements AfterViewInit, OnDestroy, OnChanges {
  public rootId = input<string>();

  private translate = inject(TranslateService);
  private permissions = inject(PermissionService);
  private map!: L.Map | null;
  private tileLayer!: L.TileLayer;

  private currentMarkers: Map<string, BotevoMarker> = new Map<string, BotevoMarker>();
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private auth = inject(AuthService);
  public selectedAsset: WritableSignal<AssetHierarchyDTO | null> = signal(null);
  private assetService = inject(AssetService);
  private assetHierarchyService = inject(AssetHierarchyService);
  private severityWebsocket = inject(SeverityService);
  private assetHierarchy!: AssetHierarchyDTO[];
  private markerClusterGroup = window.L.markerClusterGroup({
    removeOutsideVisibleBounds: true,
    iconCreateFunction: function () {
      return new L.DivIcon({});
    },
  });
  private assetDetailsSub!: Subscription;
  private severitySub!: Subscription;
  private severitySubscription?: SeveritySubscriptionHandle;

  public floorMapUrl: WritableSignal<string | null> = signal<string | null>(null);
  public floorMapPoints: FloorMapPoint[] = [];

  public loading: WritableSignal<boolean> = signal(false);
  public timeOut = createTimeoutSignal(this.loading);

  constructor() {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe({
      next: (langChange) => {
        const newLang = langChange.lang as SupportedLang;
        this.changeTiles(newLang);
      },
    });
  }

  /**
   * Called after the component's bindings have been checked for the first time.
   * If the rootId input has changed, reset the floor map URL and fetch the asset hierarchy.
   * @param changes - The changes to the component's inputs.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const rootId = changes["rootId"];
    if (!rootId?.firstChange) {
      this.floorMapUrl.set(null);
      this.getHierarchy();
    }
  }

  /**
   * Clean up resources when the component is destroyed.
   * Removes the map from the DOM, unsubscribes from the asset details subscription,
   * unsubscribes from the severity subscription, and closes the real-time severity service.
   */
  ngOnDestroy() {
    this.map?.remove();
    this.map = null;
    if (this.assetDetailsSub) {
      this.assetDetailsSub.unsubscribe();
    }
    if (this.severitySub) {
      this.severitySub.unsubscribe();
    }
    this.severitySubscription?.close();
  }
  /**
   * Lifecycle hook that is called after the component's view has been fully initialized.
   * Calls the getHierarchy method to fetch the asset hierarchy based on the root ID.
   */
  ngAfterViewInit() {
    this.getHierarchy();
  }

  /**
   * Updates the severity of the given asset in the asset hierarchy.
   * If the asset needs to be recreated, it will call the createPoints method if the floor map URL is present,
   * otherwise it will call the createChildrenMarkers method.
   * It also updates the global hierarchy variable if it exists.
   * @param incomingData - The new severity data from the real-time severity service.
   */
  private handleRealtimeSeverityUpdates(incomingData: RealtimeSeverity) {
    // Map to decide if children need to be recreated:
    const recreateMarkersNecessaryMap: Record<string, string> = {
      [ASSET_TYPES.TENANT]: ASSET_TYPES.LOCATION,
      [ASSET_TYPES.CUSTOMER]: ASSET_TYPES.LOCATION,
      [ASSET_TYPES.LOCATION]: ASSET_TYPES.BUILDING,
      [ASSET_TYPES.BUILDING]: ASSET_TYPES.GATE_SYSTEM,
    };

    let recreateMarkersNecessary = false;
    const assetMatchCheck = (assetHierarchy: AssetHierarchyDTO[]) => {
      assetHierarchy.forEach((asset) => {
        if (this.selectedAsset() && asset.assetType === incomingData.assetType && asset.id === incomingData.assetId) {
          recreateMarkersNecessary =
            recreateMarkersNecessaryMap[this.selectedAsset()!.assetType] === incomingData.assetType;
          asset.severity = incomingData.severity;
        } else if (asset.children) {
          assetMatchCheck(asset.children);
        }
      });
    };

    if (this.selectedAsset()?.children) {
      assetMatchCheck(this.selectedAsset()!.children!);
      if (recreateMarkersNecessary) {
        if (this.floorMapUrl()) {
          this.createPoints();
        } else {
          this.createChildrenMarkers();
        }
      }
    }
    // Check and update also global hierarchy variable:
    if (this.assetHierarchy) {
      assetMatchCheck(this.assetHierarchy);
    }
  }

  /**
   * Fetches the asset hierarchy and updates the component state.
   * Initializes the map and selects the first asset in the hierarchy.
   */

  private getHierarchy() {
    this.loading.set(true);
    this.assetHierarchyService
      .getAssetHierarchy(ASSET_TYPES.CUSTOMER, 4, this.rootId())
      .pipe(first())
      .subscribe((res) => {
        if (res) {
          this.assetHierarchy = res;
          this.loading.set(false);

          this.initMap();
          this.selectAsset(this.assetHierarchy[0]);
          this.createChildrenMarkers();
          // WIP: This is adjusted to FE temporary solution from service
          // It assumes that if TENANT is root asset type its children are already fetched from API.
          // If BE changes asset Hierarchy to accept TENANT as root asset and we use max Level 1 here, then it wont work.
          const rootAssetType = res[0].assetType as AssetType;
          let customerIds: string[] = [];
          if (rootAssetType === ASSET_TYPES.TENANT && res[0].children) {
            customerIds = res[0].children.map((c) => c.id);
          } else if (rootAssetType === ASSET_TYPES.CUSTOMER) {
            customerIds = res.map((c) => c.id);
          }

          if (!this.severitySubscription) {
            const subscription = this.severityWebsocket.register({ customerIds });
            this.severitySubscription = subscription;
            this.severitySub = subscription.messages$.subscribe({
              next: (incomingData) => {
                this.handleRealtimeSeverityUpdates(incomingData);
              },
              error: () => undefined,
            });
          } else {
            this.severitySubscription.replace({ customerIds });
          }
        }
      });
  }

  /**
   * Selects an asset and updates the component state accordingly.
   * Navigates to the gate system status page if the asset is a gate system
   * and the user has the necessary permissions.
   * Updates the floor map and markers for location and building assets.
   *
   * @param asset The asset to be selected.
   */
  private selectAsset(asset: AssetHierarchyDTO) {
    if (
      asset.assetType === ASSET_TYPES.GATE_SYSTEM &&
      this.permissions.hasPermission(this.permissions.allPermissions.ASSET_STATUS_DETAILS_READ)
    ) {
      this.router.navigate([`dashboard/status/gateSystem/${asset.id}`]);
      this.ngOnDestroy();
      return;
    }
    if (asset.children) {
      this.selectedAsset.set(asset);

      if (this.selectedAsset() && this.selectedAsset()!.assetType === ASSET_TYPES.LOCATION) {
        this.assetDetailsSub = this.assetService
          .getAssetDetails(this.selectedAsset()!.id, ASSET_TYPES.LOCATION)
          .subscribe(async (asset) => {
            if (asset) {
              const location = asset as Location;
              if (location.floorMapUrl) {
                const floorMapUrl = await this.extractFloorMapSource(location);
                this.floorMapUrl.set(floorMapUrl);
                this.createPoints();
                this.map?.off();
                this.map?.remove();
                this.map = null;
              } else {
                this.floorMapUrl.set(null);
                if (!this.map) {
                  setTimeout(() => {
                    this.initMap();
                    this.createChildrenMarkers();
                  }, 400);
                }
                this.createChildrenMarkers();
              }
            }
          });
      } else if (this.selectedAsset() && this.selectedAsset()!.assetType === ASSET_TYPES.BUILDING) {
        this.assetDetailsSub = this.assetService
          .getAssetDetails(this.selectedAsset()!.id, ASSET_TYPES.BUILDING)
          .subscribe(async (asset) => {
            if (asset) {
              const building = asset as Building;
              if (building.floorMapUrl) {
                const floorMapUrl = await this.extractFloorMapSource(building);
                this.floorMapUrl.set(floorMapUrl);
                this.createPoints();
                this.map?.off();
                this.map?.remove();
                this.map = null;
              } else {
                this.floorMapUrl.set(null);
              }
            }
          });
      } else {
        this.floorMapUrl.set(null);
        if (!this.map) {
          setTimeout(() => {
            this.initMap();
            this.createChildrenMarkers();
          }, 400);
        }

        this.createChildrenMarkers();
      }
    }
  }

  /**
   * Extracts the floor map source from the asset's location.
   * If the floor map source is a PDF, it is loaded as an image.
   * @param asset The asset to extract the floor map source from.
   * @returns The floor map source as a URL, or null if it does not exist.
   */
  private async extractFloorMapSource(asset: Building | Location) {
    if (asset.floorMapUrl) {
      const fileType = this.getFileType(asset.floorMapUrl);
      switch (fileType) {
        case "image":
          return asset.floorMapUrl;

        case "pdf":
          return (await this.loadPdfAsImage(asset.floorMapUrl)) ?? "";

        default:
          return null;
      }
    } else {
      return null;
    }
  }
  /**
   * Loads a PDF as an image.
   * @param pdfUrl The URL of the PDF to load.
   * @returns A data URL of the PDF as an image, or null if an error occurs.
   */
  private async loadPdfAsImage(pdfUrl: string) {
    try {
      GlobalWorkerOptions.workerSrc = "assets/pdf.worker.min.mjs";

      const loadingTask = getDocument({ url: pdfUrl });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1); // Get the first page

      const scale = 2; // Adjust for better resolution
      const viewport = page.getViewport({ scale });

      // Create a canvas element
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d")!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderContext = {
        canvas,
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;

      // Convert canvas to image URL
      return canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Error loading PDF:", error);
      return;
    }
  }

  /**
   * Determines the file type of the given URL based on the file extension.
   * @param url The URL of the file to check.
   * @returns "pdf" if the URL ends with a PDF extension, "image" if it ends
   * with an image extension (png, jpeg, jpg), and "other" if it does not match
   * any known patterns.
   */

  private getFileType(url: string): string {
    if (PDF_PATTERN.test(url)) {
      return "pdf";
    } else if (IMAGE_PATTERN.test(url)) {
      return "image";
    } else {
      return "other";
    }
  }
  /**
   * Populates the floorMapPoints array with points derived from the selected
   * asset's children, if available. Each point includes the asset's ID, label,
   * status, and coordinates. Triggers change detection after a delay to ensure
   * elements are rendered.
   */

  private createPoints() {
    this.floorMapPoints = [];

    if (this.selectedAsset() && this.selectedAsset()!.children && this.selectedAsset()!.children!.length > 0) {
      this.selectedAsset()!.children!.forEach((asset) => {
        if (asset.position && asset.position.latitude && asset.position.longitude && asset.label) {
          this.floorMapPoints.push({
            id: asset.id,
            label: asset.label,
            status: asset.severity ?? null,
            core: asset.core ?? null,
            x: asset.position.latitude,
            y: asset.position.longitude,
          });
        }
      });
    }

    // Temporary solution: run this method after some time to wait for element to render. TODO: Check method to make sure image element is rendered
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 700);
  }

  /**
   * Calculates the pixel coordinates for a given point on the floor map.
   * The point's coordinates are expected to be relative values between 0 and 1.
   *
   * @param point An object containing relative x and y coordinates.
   * @returns An object with `top.px` and `left.px` properties if the calculation is successful,
   *          otherwise an empty object if the input is invalid or the map element is not found.
   */

  public getCoordinates(point: { x: number; y: number }) {
    const rect = document.getElementById("mapPageFloorMap")?.getBoundingClientRect();

    if (point && point.x <= 1 && point.y <= 1 && rect && rect.height > 0 && rect.width > 0) {
      return {
        "top.px": point.y * rect.height,
        "left.px": point.x * rect.width,
      };
    } else {
      return {};
    }
  }

  /**
   * Calculates the background image of a floor map point based on its status.
   * If the status is OK, CRITICAL or MAJOR, it returns a different icon.
   * Otherwise, it returns the default icon.
   * @param point An object containing the status of the point.
   * @returns An object with a background-image property.
   */
  public getPointImage(point: FloorMapPoint) {
    if (!point.core) {
      return SEVERITY.UNKNOWN.iconUrl;
    } else {
      return this.getIconUrl(point.status);
    }
  }

  /**
   * Returns the URL of the icon corresponding with the given severity.
   * If the severity is OK, MINOR, CRITICAL, or MAJOR, it returns the corresponding icon URL.
   * Otherwise, it returns the URL of the default icon.
   * @param severity The severity of the asset to get the icon for.
   * @returns The URL of the icon corresponding with the given severity.
   */
  private getIconUrl(severity: string | null) {
    switch (severity) {
      case SEVERITY.OK.status:
        return SEVERITY.OK.iconUrl;
      case SEVERITY.MINOR.status:
        return SEVERITY.MINOR.iconUrl;
      case SEVERITY.CRITICAL.status:
        return SEVERITY.CRITICAL.iconUrl;
      case SEVERITY.MAJOR.status:
        return SEVERITY.MAJOR.iconUrl;
      default:
        return SEVERITY.UNKNOWN.iconUrl;
    }
  }
  /**
   * Goes back to the parent asset of the currently selected asset.
   * If the parent cannot be found, does nothing.
   */
  public goBack() {
    if (this.selectedAsset() === null) return;
    const parent = this.findParent(this.assetHierarchy, this.selectedAsset()!.id, this.selectedAsset()!.assetType);
    if (parent) {
      // Having this additional check to skip a level if going from location back to customer (in case of tenant admin map needs to go back to tenant level)
      if (parent.assetType === ASSET_TYPES.CUSTOMER && this.auth.getRole() === USER_ROLES.TENANT_ADMIN) {
        const grandParent = this.findParent(this.assetHierarchy, parent.id, ASSET_TYPES.CUSTOMER);
        if (grandParent) {
          this.selectAsset(grandParent);
        }
      } else {
        this.selectAsset(parent);
      }
    }
  }

  /**
   * Recursively searches the asset hierarchy to find the direct parent of a specified asset.
   * @param assetHierarchy The list of assets to search through.
   * @param targetId The ID of the asset whose parent is being sought.
   * @param targetType The type of the asset whose parent is being sought.
   * @returns The parent asset if found, otherwise null.
   */
  private findParent(
    assetHierarchy: AssetHierarchyDTO[],
    targetId: string,
    targetType: string
  ): AssetHierarchyDTO | null {
    for (const asset of assetHierarchy) {
      // Check if any of the node's children have the targetId
      if (asset.children) {
        for (const child of asset.children) {
          if (child.id === targetId && child.assetType === targetType) {
            // If a child with the targetId is found, return the current node as the direct parent
            return asset;
          }
        }
        // Recursively search in the children nodes
        const result = this.findParent(asset.children, targetId, targetType);
        if (result) {
          return result;
        }
      }
    }
    // Return null if no parent is found
    return null;
  }

  /**
   * Handles a click on a floor map point by selecting the asset associated with the point.
   * The asset is expected to be a child of the currently selected asset.
   * @param point The point that was clicked, containing the ID of the associated asset.
   */
  public goToNextLevel(point: FloorMapPoint) {
    const clickedAsset = this.selectedAsset()?.children?.find((asset) => asset.id === point.id);
    if (clickedAsset) {
      this.selectAsset(clickedAsset);
    }
  }

  /**
   * Given a list of asset status strings, returns a color string that represents the
   * overall severity of the group. If the list contains "CRITICAL", returns red,
   * if it contains "MAJOR", returns orange, and otherwise returns green.
   * @param cluster A list of asset status strings.
   * @returns A color string in the format "#rrggbb".
   */
  private getClusterSeverityColor(cluster: string[]): string {
    if (cluster.includes(SEVERITY.CRITICAL.status)) {
      return SEVERITY.CRITICAL.color;
    } else if (cluster.includes(SEVERITY.MAJOR.status)) {
      return SEVERITY.MAJOR.color;
    } else if (cluster.includes(SEVERITY.MINOR.status)) {
      return SEVERITY.MINOR.color;
    } else if (cluster.includes(SEVERITY.OK.status)) {
      return SEVERITY.OK.color;
    } else {
      return SEVERITY.UNKNOWN.color;
    }
  }

  /**
   * Creates markers for the children of the currently selected asset.
   * If the selected asset is a tenant, it recursively searches for its children.
   * It creates a cluster group and adds all the children markers to it.
   * When a marker is clicked, it selects the associated asset.
   * The cluster group is then added to the map.
   */
  private createChildrenMarkers() {
    let assetChildren = this.selectedAsset()?.children;
    this.map?.removeLayer(this.markerClusterGroup);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this;
    this.markerClusterGroup = window.L.markerClusterGroup({
      removeOutsideVisibleBounds: true,
      iconCreateFunction: function (cluster) {
        return new L.DivIcon({
          html: `<div style="background-color: ${_this.getClusterSeverityColor(cluster.getAllChildMarkers().map((marker: any) => marker.status))}; border-radius: 50%; width: 30px; height: 30px; line-height: 30px; text-align: center; color: white;">
          ${cluster.getChildCount()}
        </div>`,
          className: "customDivBotevo",
        });
      },
    });
    if (assetChildren && assetChildren.length > 0) {
      // In case of tenant the first children level (customers) needs to be skipped and go straight to locations:
      if (this.selectedAsset() && this.selectedAsset()!.assetType === ASSET_TYPES.TENANT) {
        assetChildren = [];
        this.selectedAsset()?.children?.forEach((asset) => {
          if (assetChildren && asset.children) {
            assetChildren.push(...asset.children);
          }
        });
      }
      assetChildren.forEach((asset) => {
        if (asset.position && asset.label) {
          const marker = this.initMarker(
            asset.position,
            asset.severity ? asset.severity : SEVERITY.UNKNOWN.status,
            asset.label,
            asset.id,
            asset.assetType
          );
          if (marker) {
            marker.on("click", () => {
              this.selectAsset(asset);
            });
            this.markerClusterGroup.addLayer(marker);
          }
        }
      });
      if (this.map) {
        this.markerClusterGroup.addTo(this.map);
      }
    }
  }

  /**
   * Initializes a marker on the map with the given latitude and longitude.
   * @param position The position of the marker.
   * @param status The status of the asset.
   * @param assetName The name of the asset.
   * @param assetId The id of the asset.
   * @param assetType The type of the asset.
   * @returns a new marker if the position is valid, null otherwise
   */
  private initMarker(position: Position, status: string, assetName: string, assetId: string, assetType: string) {
    const lat = position.latitude;
    const lng = position.longitude;
    if (lat && lng) {
      const marker = new BotevoMarker(
        [lat, lng],
        {
          icon: this.getMapIcon(status, assetName),
        },
        status
      );

      this.currentMarkers.set(assetId + assetType, marker);
      return marker;
    } else {
      return null;
    }
  }

  /**
   * Returns a Leaflet icon object with a divIcon containing the icon and the asset name
   * based on the given status and asset name.
   * @param status The status of the asset
   * @param assetName The name of the asset
   * @returns A Leaflet icon object with a divIcon containing the icon and the asset name
   */
  private getMapIcon(status: string, assetName: string) {
    const iconUrl: string = this.getIconUrl(status);
    return L.divIcon({
      className: "mapDivIcon",
      html: `
        <div class="markerContainer">

          <img src="${iconUrl}" style="width: 25px!important; height: 25px!important;"/> 
          <span class="markerLabel" >${assetName}</span>

      </div>
      `,
      iconSize: [10, 10], // size of the icon
    });
  }

  /**
   * Initializes the map, removing any existing map instance and setting up
   * the necessary configuration. This function is called when the component
   * is initialized and when the user navigates to a new asset.
   */
  private initMap() {
    if (this.map) {
      this.map.off(); // Remove all event listeners
      this.map.remove(); // Properly destroy the map
      this.map = null; // Clear the reference to the map instance
    }
    const mapContainer = document.getElementById("map");
    if (mapContainer) {
      this.map = L.map("map", environment.mapConfig).setView([51.1642292, 10.4541194], 6);
      const currentLang: SupportedLang = this.translate.currentLang as SupportedLang;

      const tilesUrl = environment.mapTileUrl[currentLang];
      if (tilesUrl) {
        this.tileLayer = L.tileLayer(tilesUrl).addTo(this.map);
        setTimeout(() => {
          this.map?.invalidateSize();
        }, 50);
      }
    }
  }

  /**
   * Changes the map tiles to match the specified language.
   * Removes the current tile layer and adds a new one based on the new language.
   * Invalidates the map size to ensure proper rendering after changing tiles.
   *
   * @param newLang The new language for which to update the map tiles.
   */
  private changeTiles(newLang: SupportedLang) {
    if (this.map && this.tileLayer) {
      this.map.removeLayer(this.tileLayer);
      const tilesUrl = environment.mapTileUrl[newLang];
      this.tileLayer = L.tileLayer(tilesUrl).addTo(this.map);
      setTimeout(() => {
        this.map?.invalidateSize();
      }, 50);
    }
  }
}
